import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../ui/dialog';
import { Mail, Tag, Plus, Edit, Trash2, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMailing() {
  const [mailingTags, setMailingTags] = useState([]);
  const [mailingSettings, setMailingSettings] = useState({ default_sender_email: '', default_sender_name: 'Vitalis Sports' });
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [newTag, setNewTag] = useState({ name: '', color: '#3B82F6' });

  const fetchMailingTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mailing/tags`);
      setMailingTags(res.data);
    } catch (err) {
      console.error('Error fetching mailing tags:', err);
    }
  }, []);

  const fetchMailingSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mailing/settings`);
      setMailingSettings(res.data);
    } catch (err) {
      console.error('Error fetching mailing settings:', err);
    }
  }, []);

  useEffect(() => {
    fetchMailingTags();
    fetchMailingSettings();
  }, [fetchMailingTags, fetchMailingSettings]);

  const handleSaveMailingSettings = async () => {
    try {
      await axios.put(`${API}/mailing/settings`, mailingSettings);
      toast.success('Paramètres mailing enregistrés');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur'));
    }
  };

  const handleSaveTag = async () => {
    try {
      if (editingTag) {
        await axios.put(`${API}/mailing/tags/${editingTag.id}`, newTag);
        toast.success('Tag modifié');
      } else {
        await axios.post(`${API}/mailing/tags`, newTag);
        toast.success('Tag créé');
      }
      setTagDialogOpen(false);
      setEditingTag(null);
      setNewTag({ name: '', color: '#3B82F6' });
      fetchMailingTags();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur'));
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Supprimer ce tag ?')) return;
    try {
      await axios.delete(`${API}/mailing/tags/${tagId}`);
      toast.success('Tag supprimé');
      fetchMailingTags();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur'));
    }
  };

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Paramètres expéditeur */}
        <div className="p-6 border border-border rounded-lg space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Expéditeur par défaut
          </h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email expéditeur</Label>
              <Input
                type="email"
                value={mailingSettings.default_sender_email || ''}
                onChange={(e) => setMailingSettings({ ...mailingSettings, default_sender_email: e.target.value })}
                placeholder="noreply@vitalis-sports.ch"
              />
            </div>
            <div className="space-y-2">
              <Label>Nom expéditeur</Label>
              <Input
                value={mailingSettings.default_sender_name || ''}
                onChange={(e) => setMailingSettings({ ...mailingSettings, default_sender_name: e.target.value })}
                placeholder="Vitalis Sports"
              />
            </div>
            <Button onClick={handleSaveMailingSettings}>
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Tags */}
        <div className="p-6 border border-border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Tags
            </h3>
            <Button size="sm" onClick={() => { setEditingTag(null); setNewTag({ name: '', color: '#3B82F6' }); setTagDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter
            </Button>
          </div>
          <div className="space-y-2">
            {mailingTags.map(tag => (
              <div key={tag.id} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: tag.color }} />
                  <span>{tag.name}</span>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setEditingTag(tag); setNewTag({ name: tag.name, color: tag.color }); setTagDialogOpen(true); }}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteTag(tag.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
            {mailingTags.length === 0 && (
              <p className="text-muted-foreground text-center py-4">Aucun tag</p>
            )}
          </div>
        </div>
      </div>

      {/* Tag Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Modifier le tag' : 'Nouveau tag'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom du tag</Label>
              <Input value={newTag.name} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} placeholder="Ex: Tennis, VIP..." />
            </div>
            <div>
              <Label>Couleur</Label>
              <div className="flex gap-2 items-center">
                <input type="color" value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} className="w-12 h-10 rounded border cursor-pointer" />
                <Input value={newTag.color} onChange={(e) => setNewTag({ ...newTag, color: e.target.value })} className="w-32" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveTag}>{editingTag ? 'Modifier' : 'Créer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
